/**
 * TimelineEventStore — data-access for the OPTIONAL explicit timeline events.
 *
 * Strict layering: this is the ONLY timeline module that talks to the API, and
 * it does so exclusively through the {@link EngineeringVectorApi} facade
 * (`api.documents<TimelineEvent>('timelineEvents')` + `api.identityProvider`).
 * It never imports ApiClient or calls fetch. The derived timeline itself is
 * pure ({@link ./timeline}); this store only persists the manual milestones /
 * notes that no other entity captures.
 *
 * Persistence model: each explicit event is one document with id `tle_<uuid>`
 * in the real 'timelineEvents' generic-store collection (DDB pointer row + S3
 * body). Every field is short, so every field is mirrored onto the pointer row
 * and {@link listSummaries} reconstructs full summaries WITHOUT hydrating S3.
 *
 * Deletion: the API layer exposes no delete op, so {@link softDelete} sets a
 * `deleted` flag; queries filter it out. NO backend changes required.
 */

import type { EngineeringVectorApi } from '../../api';
import type { DocumentStore } from '../../api';
import type { StringMap } from '../../api';
import type {
  TimelineEvent,
  TimelineEventDraft,
  TimelineEventKind,
  TimelineEventSummary,
} from './types';
import { timelineEventDocId } from './timelineModel';

export const TIMELINE_EVENTS_COLLECTION = 'timelineEvents';

const VALID_KINDS: TimelineEventKind[] = [
  'workstream_started',
  'document_created',
  'document_approved',
  'decision_made',
  'blocker_raised',
  'blocker_resolved',
  'milestone',
  'custom',
];

function normalizeKind(value: string | undefined): TimelineEventKind {
  return VALID_KINDS.includes(value as TimelineEventKind)
    ? (value as TimelineEventKind)
    : 'custom';
}

/** Pointer-row index attribute keys. */
const ROW = {
  primaryIdentifier: 'primaryIdentifier',
  collection: 'collection',
  title: 'title',
  description: 'description',
  date: 'date',
  kind: 'kind',
  engineer: 'engineer',
  relatedGoalId: 'relatedGoalId',
  relatedWorkstreamId: 'relatedWorkstreamId',
  relatedServiceId: 'relatedServiceId',
  relatedDocId: 'relatedDocId',
  relatedDecisionId: 'relatedDecisionId',
  relatedStoryId: 'relatedStoryId',
  createdBy: 'createdBy',
  createdAt: 'createdAt',
  lastUpdated: 'lastUpdated',
  deleted: 'deleted',
} as const;

export class TimelineEventStore {
  private readonly docs: DocumentStore<TimelineEvent>;

  constructor(private readonly api: EngineeringVectorApi) {
    this.docs = api.documents<TimelineEvent>(TIMELINE_EVENTS_COLLECTION);
  }

  private indexAttributes(event: TimelineEvent): StringMap {
    return {
      [ROW.title]: event.title ?? '',
      [ROW.description]: event.description ?? '',
      [ROW.date]: event.date ?? '',
      [ROW.kind]: event.kind,
      [ROW.engineer]: event.engineer ?? '',
      [ROW.relatedGoalId]: event.relatedGoalId ?? '',
      [ROW.relatedWorkstreamId]: event.relatedWorkstreamId ?? '',
      [ROW.relatedServiceId]: event.relatedServiceId ?? '',
      [ROW.relatedDocId]: event.relatedDocId ?? '',
      [ROW.relatedDecisionId]: event.relatedDecisionId ?? '',
      [ROW.relatedStoryId]: event.relatedStoryId ?? '',
      [ROW.createdBy]: event.createdBy ?? '',
      [ROW.createdAt]: event.createdAt ?? '',
      [ROW.lastUpdated]: event.lastUpdated ?? '',
      [ROW.deleted]: event.deleted ? 'true' : 'false',
    };
  }

  private toSummary(row: StringMap): TimelineEventSummary {
    return {
      id: row[ROW.primaryIdentifier] ?? '',
      title: row[ROW.title] ?? '',
      description: row[ROW.description] || undefined,
      date: row[ROW.date] ?? '',
      kind: normalizeKind(row[ROW.kind]),
      engineer: row[ROW.engineer] || undefined,
      relatedGoalId: row[ROW.relatedGoalId] || undefined,
      relatedWorkstreamId: row[ROW.relatedWorkstreamId] || undefined,
      relatedServiceId: row[ROW.relatedServiceId] || undefined,
      relatedDocId: row[ROW.relatedDocId] || undefined,
      relatedDecisionId: row[ROW.relatedDecisionId] || undefined,
      relatedStoryId: row[ROW.relatedStoryId] || undefined,
      createdBy: row[ROW.createdBy] ?? '',
      createdAt: row[ROW.createdAt] ?? '',
      lastUpdated: row[ROW.lastUpdated] ?? '',
    };
  }

  /** Lists all non-deleted explicit-event summaries (defensively scoped). */
  async listSummaries(): Promise<TimelineEventSummary[]> {
    const rows = await this.docs.list();
    return rows
      .filter(
        (r) =>
          r[ROW.collection] === TIMELINE_EVENTS_COLLECTION &&
          r[ROW.deleted] !== 'true' &&
          !!r[ROW.primaryIdentifier],
      )
      .map((r) => this.toSummary(r));
  }

  /** Loads a single explicit event by id (hydrates the S3 body). */
  async get(id: string): Promise<TimelineEvent | undefined> {
    return this.docs.get(id);
  }

  /** Creates a new explicit event. */
  async create(draft: TimelineEventDraft): Promise<TimelineEvent> {
    const identity = await this.api.identityProvider.getIdentity();
    const event = this.fromDraft(timelineEventDocId(), draft, identity.alias, undefined);
    await this.docs.save(event.id, event, this.indexAttributes(event));
    return event;
  }

  /** Applies a draft's fields to an existing explicit event in place. */
  async applyDraft(event: TimelineEvent, draft: TimelineEventDraft): Promise<TimelineEvent> {
    const updated = this.fromDraft(event.id, draft, event.createdBy, event.createdAt);
    return this.update(updated);
  }

  /** Persists a full explicit event in place (refreshes lastUpdated). */
  async update(event: TimelineEvent): Promise<TimelineEvent> {
    const updated: TimelineEvent = {
      ...event,
      lastUpdated: new Date().toISOString(),
      deleted: false,
    };
    await this.docs.update(updated.id, updated, this.indexAttributes(updated));
    return updated;
  }

  /** Soft-deletes an explicit event. */
  async softDelete(id: string): Promise<void> {
    const existing = await this.docs.get(id);
    if (!existing) return;
    const deleted: TimelineEvent = { ...existing, deleted: true };
    await this.docs.update(id, deleted, this.indexAttributes(deleted));
  }

  private fromDraft(
    id: string,
    draft: TimelineEventDraft,
    createdBy: string,
    existingCreatedAt: string | undefined,
  ): TimelineEvent {
    const now = new Date().toISOString();
    const clean = (s?: string) => {
      const t = (s ?? '').trim();
      return t ? t : undefined;
    };
    return {
      id,
      title: draft.title.trim(),
      description: clean(draft.description),
      date: draft.date.trim(),
      kind: normalizeKind(draft.kind),
      engineer: clean(draft.engineer),
      relatedGoalId: draft.relatedGoalId || undefined,
      relatedWorkstreamId: draft.relatedWorkstreamId || undefined,
      relatedServiceId: draft.relatedServiceId || undefined,
      relatedDocId: draft.relatedDocId || undefined,
      relatedDecisionId: draft.relatedDecisionId || undefined,
      relatedStoryId: draft.relatedStoryId || undefined,
      createdBy,
      createdAt: existingCreatedAt ?? now,
      lastUpdated: now,
      deleted: false,
    };
  }
}
